import { useCurrentEditor } from '@tiptap/react';
import { Table as TableIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../tooltip/tooltip';

const MAX_ROWS = 8;
const MAX_COLS = 8;

/**
 * Toolbar button: inserts a table via an 8×8 size picker.
 */
export const TableInsertButton = () => {
  const { editor } = useCurrentEditor();
  const { t } = useTranslation();
  const gridRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState<{ rows: number; cols: number } | null>(null);

  if (!editor) {
    return null;
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSelection(null);
    }
  };

  const insert = (rows: number, cols: number) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    handleOpenChange(false);
  };

  const focusCell = (rowIdx: number, colIdx: number) => {
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-cell="${rowIdx}-${colIdx}"]`)?.focus();
  };

  const onGridKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const dr = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    const dc = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
    if (dr === 0 && dc === 0) {
      return;
    }

    event.preventDefault();

    const current = selection ?? { rows: 1, cols: 1 };
    const nextRows = Math.max(1, Math.min(MAX_ROWS, current.rows + dr));
    const nextCols = Math.max(1, Math.min(MAX_COLS, current.cols + dc));

    setSelection({ rows: nextRows, cols: nextCols });
    focusCell(nextRows - 1, nextCols - 1);
  };

  const hint = selection ? `${selection.rows} × ${selection.cols}` : t('table-insert-hint');

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={<PopoverTrigger />}
          className="tiptap-toolbar-button"
          aria-label={t('table-insert')}
          onMouseDown={(event) => event.preventDefault()}
        >
          <TableIcon />
        </TooltipTrigger>
        <TooltipContent>{t('table-insert')}</TooltipContent>
      </Tooltip>
      <PopoverContent side="bottom" align="start" className="w-auto">
        <div className="text-center text-muted-foreground text-xs">{hint}</div>
        {/* biome-ignore lint/a11y/useSemanticElements: this is a size picker driven by ARIA grid semantics, not a data table */}
        <div
          ref={gridRef}
          role="grid"
          aria-label={t('table-insert')}
          className="tiptap-table-grid"
          onKeyDown={onGridKeyDown}
        >
          {Array.from({ length: MAX_ROWS }, (_, r) =>
            Array.from({ length: MAX_COLS }, (_, c) => {
              const filled = selection ? r < selection.rows && c < selection.cols : false;
              const isActive = selection ? r === selection.rows - 1 && c === selection.cols - 1 : r === 0 && c === 0;
              return (
                // biome-ignore lint/a11y/useSemanticElements: gridcell on a <button> keeps it focusable and clickable inside the role="grid" pattern
                <button
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable fixed grid
                  key={`${r}-${c}`}
                  type="button"
                  role="gridcell"
                  tabIndex={isActive ? 0 : -1}
                  data-cell={`${r}-${c}`}
                  aria-label={`${r + 1} × ${c + 1}`}
                  aria-selected={filled}
                  className="tiptap-table-grid-cell"
                  data-filled={filled || undefined}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setSelection({ rows: r + 1, cols: c + 1 })}
                  onClick={() => insert(r + 1, c + 1)}
                />
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
